import { defineStore, storeToRefs } from 'pinia'
import { type Raw, computed, shallowRef, watch } from 'vue'

import { useAdProvidersSettings } from '@/composables/ad-providers/use-ad-settings'
import { useConfig } from '@/composables/config'
import { useClidHelpers } from '@/composables/use-clid-helpers'
import { AdService, type AdServiceOptions, type ProviderKey, type ProviderConfig } from '@/modules/adv/ad-service'
import type { HeaderBiddingOptions } from '@/modules/adv/header-bidding/types'
import { initProviders } from '@/modules/adv/provider-initializer'
import { TargetingMapping } from '@/modules/adv/targgeting-mapping'
import { LogLevel, setAdConsoleLoggerLevel } from '@/modules/adv/utils/ad-console-logger'
import { useAdBlockStore } from '@/store/ad-block-store'
import { useAppStore } from '@/store/app-store'
import { useLoggerStore } from '@/store/logger'
import { useUserStore } from '@/store/user-store/user-store'
import { type AdProvidersSettings } from '@/types'
import { getHashedEmailFromCookie, updateCookie, YAHOO_ID_SIGNAL_COOKIE } from '@/utils/cookie-parser'
import { sha256hex } from '@/utils/crypto'
import { transformUACtoAdTargetings } from '@/utils/uac-cookie'

import { getCurrentProvidersSettings } from './current-providers-settings'

export const useAdsStore = defineStore('ads', () => {
    const { logEvent, startRumEvent, registerPageHideFallbackEvent } = useLoggerStore()
    const {
        auid,
        clid,
        isOppoWL,
        widgetId,
        platform,
        isDebugMode,
        userAcquisitionParams,
        isGameDistribution,
        isExportOnWidget,
        isPortalEnv,
        isNativeAppEnv,
        experiments,
        countryCode,
    } = storeToRefs(useAppStore())
    const { adBlockStatus } = storeToRefs(useAdBlockStore())

    const userStore = useUserStore()

    const adService = shallowRef<Raw<AdService>>()
    const providersSettings = useAdProvidersSettings()

    const { clidConfig } = useClidHelpers()

    const { VUE_APP_AD_MANAGER_ACCOUNT } = useConfig()

    const commonTargeting = computed(() => {
        return {
            playgama_clid: clid.value,
            widget_id: widgetId.value || '',
            [TargetingMapping.AD_BLOCK_STATUS]: adBlockStatus.value,
            [TargetingMapping.IS_LOGGED_IN]: '0',
            ...transformUACtoAdTargetings(userAcquisitionParams.value),
        }
    })

    function setAdServiceConfig(options: {
        targeting?: AdServiceOptions['targeting']
        auid: string
        isGoogleProvidersEnabled: boolean
    }) {
        const targeting: AdServiceOptions['targeting'] = { ...commonTargeting.value, ...options.targeting }

        //For Experiments with ads revenue tracking
        const exp = experiments.value?.['use_aud3']?.sample || experiments.value?.['preroll_interstitial_delay']?.sample
        if (exp) {
            targeting.exp = exp
        }

        const headerBiddingConfig: HeaderBiddingOptions = {
            debug: isDebugMode.value,
            platform: platform.value,
            pgAuid: auid.value,
            adSource: isNativeAppEnv.value ? 'native' : 'portal',
            hashedEmail: getHashedEmailFromCookie(),
            logEvent,
        }

        const currentProviders = getCurrentProvidersSettings({
            clid: clid.value,
            providersSettings,
            clidConfig: clidConfig.value,
            isGameDistribution: isGameDistribution.value,
            isOppoWL: isOppoWL.value,
            isExportOnWidget: isExportOnWidget.value,
            isPartnerEmbed: !isPortalEnv.value,
            isNativeAppEnv: isNativeAppEnv.value,
            isGoogleProvidersEnabled: options.isGoogleProvidersEnabled,
        })
        const currentProvidersSettings = Object.entries(currentProviders).reduce<
            Partial<Record<ProviderKey, ProviderConfig[]>>
        >((acc, [key, value]) => {
            acc[key as ProviderKey] = (value as AdProvidersSettings[]).map((s) => ({
                type: s.type,
                timeout: s.options.timeout,
            }))
            return acc
        }, {})

        const providersInitConfig = {
            targeting,
            startRumEvent,
            auid: options.auid,
        }

        if (headerBiddingConfig && !isPortalEnv.value) {
            headerBiddingConfig.adSource = 'export'
        }

        const service = new AdService({
            getCountryCode: () => Promise.resolve(countryCode.value),
            consentAccount: VUE_APP_AD_MANAGER_ACCOUNT,
            targeting,
            startRumEvent,
            logEvent,
            registerPageHideFallbackEvent,
            auid: options.auid,
            headerBiddingConfig,
            providersSettings: currentProvidersSettings,
        })

        userStore.getUser().then(async () => {
            if (userStore.isCompanyEmployeeLoggedIn) {
                setAdConsoleLoggerLevel(LogLevel.debug)
            }
            if (userStore.user?.email && userStore.user?.email !== '') {
                const email = await sha256hex(userStore.user.email)
                updateCookie(YAHOO_ID_SIGNAL_COOKIE, email, 1000 * 50 * 60 * 24 * 30) // set cookie for 30 days
            }
        })

        adService.value = service

        service.consentManager.ready.then(() => {
            Object.entries(currentProviders).forEach(([type, settings]) => {
                initProviders(settings, providersInitConfig, (index, provider) => {
                    service.setProvider(index, provider, type as ProviderKey)
                })
            })
        })
    }

    function updateTargeting(targeting: AdServiceOptions['targeting']) {
        adService.value?.updateTargeting(targeting)
    }

    watch(adBlockStatus, (status) => {
        updateTargeting({
            [TargetingMapping.AD_BLOCK_STATUS]: status,
        })
    })

    return {
        adService,
        setAdServiceConfig,
        updateTargeting,
    }
})
